"use client";

import { c as _c } from "react/compiler-runtime";
import { AnyTranslationsFn } from "@/types/locale";
import { CustomExternalToast, CustomToastT } from "@/types/sonner";
import { DateTime, Interval } from "luxon";
import { toast } from "sonner";
import React, { useEffect } from "react";
import { useTheme } from "next-themes";
import { Toaster } from "@components/ui/sonner";
import { useAnyTranslations } from "@lib/i18n/utils";
import { useAppDispatch, useAppSelector } from "@lib/redux/hooks";
import { selectNotificationsToasts } from "@features/notifications/notifications-selectors";
import { dropNotificationToasts } from "@features/notifications/notifications-slice";
import { notificationsTools } from "@features/notifications/notifications-tools";
import { NotificationMessage } from "@features/notifications/notifications-types";
import { AssignmentNotificationsTrigger } from "@features/notifications/components/assignment-notifications-trigger";

/**
 * Processes a single notification message, handling deduplication and displaying it as a toast.
 *
 * This function implements a deduplication strategy to prevent spamming the user with identical notifications.
 * - For all notifications, it checks if a toast with the same `id` has already been displayed.
 * - For error notifications, it applies a more advanced check: it prevents showing an error if another error
 *   with the same status, type, and URL has been shown within the last 5 minutes. This is to avoid
 *   flooding the UI with repeated error messages from, for example, failing background polling.
 *
 * If the notification is not a duplicate, it's enhanced for display (e.g., localizing titles) and
 * then shown using the appropriate toast type based on its importance level.
 *
 * @param {NotificationMessage} t - The notification message object to process.
 * @param tAny - The translation function for internationalization.
 */
const processNotificationMessages = (t: NotificationMessage, tAny: AnyTranslationsFn) => {
  const history = toast.getHistory();

  // De-duplication check: if a notification with this ID was already shown, ignore it.
  if (history.map(h => h.id).includes(t.id)) {
    return;
  }

  // Advanced de-duplication for error messages to prevent spam.
  if (t.payload.error) {
    const tDate = DateTime.fromMillis(t.created);
    const isRecentDuplicateError = history.some((h: CustomToastT) =>
    // Check if the historic toast is a similar error
    h.error?.status === t.payload.error?.status && h.error?.context?.operationType === t.payload.error?.context?.operationType && h.error?.url === t.payload.error?.url &&
    // Check if it appeared within the last 5 minutes.
    Interval.fromDateTimes(DateTime.fromMillis(h.created), tDate).length("minutes") < 5);
    if (isRecentDuplicateError) {
      return;
    }
  }

  // Enhance the notification message with localized titles and descriptions.
  const message = notificationsTools.upgradeNotificationMessage(t, tAny);
  const toastsOptions: CustomExternalToast = {
    id: message.id,
    duration: message.payload.error ? 10000 : 5000,
    // Toasts are visible for 5 seconds.
    position: "top-right",
    description: message.payload.description,
    // Pass custom error data for advanced deduplication.
    error: message.payload.error,
    created: message.created
  };

  // Display the toast with a style corresponding to its importance level.
  if (message.importance === "error") toast.error(message.payload.title, toastsOptions);
  if (message.importance === "warn") toast.warning(message.payload.title, toastsOptions);
  if (message.importance === "info") toast.info(message.payload.title, toastsOptions);
  if (message.importance === "success") toast.success(message.payload.title, toastsOptions);
};

/**
 * Manages the lifecycle of notifications, pulling them from the Redux store,
 * displaying them as toasts, and then removing them from the store.
 *
 * This component acts as a bridge between the Redux notification state and the
 * UI (`sonner` toast library). It renders the `Toaster` container, which is where
 * toasts will appear. The real notification bell remains a separate hidden entry point
 * until persisted notifications have a source.
 *
 * The core logic resides in a `useEffect` hook that watches for new notifications
 * in the Redux store. When new notifications arrive, it processes and displays them
 * and then immediately dispatches an action to remove them from the store. This "consume-once"
 * pattern ensures that each notification is shown exactly once.
 */
interface NotificationContainerProps {
  isLogged?: boolean;
}
export const NotificationContainer = t0 => {
  const $ = _c(14);
  const {
    isLogged
  } = t0;
  const {
    resolvedTheme
  } = useTheme();
  const tAny = useAnyTranslations();
  const toasts = useAppSelector(selectNotificationsToasts);
  const dispatch = useAppDispatch();
  let t1;
  let t2;
  if ($[0] !== dispatch || $[1] !== tAny || $[2] !== toasts) {
    t1 = () => {
      if (toasts.length === 0 || !dispatch || !tAny) {
        return;
      }
      toasts.forEach(t => processNotificationMessages(t, tAny));
      dispatch(dropNotificationToasts(toasts.map(_temp)));
    };
    t2 = [dispatch, toasts, tAny];
    $[0] = dispatch;
    $[1] = tAny;
    $[2] = toasts;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  const t3 = resolvedTheme as never;
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      top: "72px",
      bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
      left: "16px",
      right: "16px"
    };
    t5 = {
      top: "60px"
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3) {
    t6 = <Toaster theme={t3} richColors={true} closeButton={true} mobileOffset={t4} offset={t5} />;
    $[7] = t3;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isLogged) {
    t7 = isLogged && <AssignmentNotificationsTrigger />;
    $[9] = isLogged;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};
function _temp(t_0) {
  return t_0.id;
}